import { useEffect, useState } from 'react';
import { VIRTUAL_LOCATION_TYPE } from '../../constants/warehouseLocations';
import api from '../../services/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import { MONO_CLASS } from '../ui/Mono';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import { useToast } from '../ui/Toast';
import { BuildLocationTypeOptions, ParseDecimalInput } from './locationFields';
import { useWarehouseLabels } from './warehouseLabels';

// Tipos criáveis individualmente: racks e o staging da rua nascem pelo GERADOR
// (o staging é auto-criado com a rua) — aqui entram os endereços literais.
const GENERATOR_ONLY_TYPES = new Set(['RACK', 'STAGING_STREET']);

const EMPTY_FORM = { code: '', type: 'STAGING_DOCK', maxWeightKg: '', sector: '', notes: '' };

/**
 * Nova localização (r4 — fluxo unificado): o PRIMEIRO passo pergunta
 * "É virtual?".
 *
 * - VIRTUAL: código LIVRE (vira o token bipável, único por org) + setor +
 *   observações. Só inventário — nunca aparece no mapa. O POST acontece AQUI.
 * - FÍSICA: tipo/código/peso/etc + passo OBRIGATÓRIO de POSICIONAR NO MAPA —
 *   este modal NÃO cria a física: valida o formulário e entrega o payload à
 *   página (`onRequestPlacement({payload, code})`), que troca para o modo Mapa
 *   com `placeRequest {type:'fixed', code}`; o POST só acontece depois que o
 *   usuário clica a célula (cancelar posicionamento = NÃO cria). Princípio do
 *   dono: localização física DEVE estar posicionada no mapa.
 */
const LocationCreateModal = ({ isOpen, onClose, onCreated, onRequestPlacement }) => {
    const toast = useToast();
    const { label } = useWarehouseLabels();
    // step: 'kind' (pergunta) → 'form'
    const [step, setStep] = useState('kind');
    const [isVirtual, setIsVirtual] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setStep('kind');
            setIsVirtual(false);
            setForm(EMPTY_FORM);
            setFormError(null);
        }
    }, [isOpen]);

    const closeModal = () => {
        if (!saving) {
            onClose();
        }
    };

    const setField = (name, value) => {
        setForm((previous) => ({ ...previous, [name]: value }));
    };

    const pickKind = (virtual) => {
        setIsVirtual(virtual);
        setForm(EMPTY_FORM);
        setFormError(null);
        setStep('form');
    };

    const physicalTypeOptions = BuildLocationTypeOptions(label).filter(
        (option) => !GENERATOR_ONLY_TYPES.has(option.value),
    );

    const handleSubmit = async (event) => {
        event.preventDefault();
        const code = form.code.trim();
        if (!code) {
            setFormError({ field: 'code', message: label('wms.common.required') });
            return;
        }
        if (isVirtual && /\s/.test(code)) {
            // Token bipável: um scanner não digita espaço.
            setFormError({ field: 'code', message: label('wms.location_create.virtual_code_help') });
            return;
        }
        const sector = form.sector.trim();
        if (isVirtual && !sector) {
            setFormError({ field: 'sector', message: label('wms.common.required') });
            return;
        }

        if (!isVirtual) {
            const maxWeightKg = ParseDecimalInput(form.maxWeightKg);
            if (Number.isNaN(maxWeightKg) || (maxWeightKg !== null && maxWeightKg <= 0)) {
                setFormError({ field: 'maxWeightKg', message: 'Informe um número maior que zero (ou deixe vazio).' });
                return;
            }
            // FÍSICA: nada de POST aqui — a página assume com o placeRequest
            // (posicionar no mapa é obrigatório; o clique na célula cria).
            const payload = {
                code,
                type: form.type,
                ...(maxWeightKg !== null ? { max_weight_kg: maxWeightKg } : {}),
                ...(sector ? { sector } : {}),
                ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
            };
            onRequestPlacement?.({ payload, code });
            onClose();
            return;
        }

        const payload = {
            code,
            type: VIRTUAL_LOCATION_TYPE,
            sector,
            ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
        };
        setSaving(true);
        setFormError(null);
        try {
            await api.createWarehouseLocation(payload);
            toast.success(label('wms.location_create.created_title'));
            onCreated?.();
            onClose();
        } catch (error) {
            if (error?.status === 400 || error?.status === 409) {
                // 400 = código fora do padrão; 409 = código já em uso.
                const serverMessage =
                    error.message && error.message !== 'Unknown error' ? error.message : label('wms.common.conflict');
                setFormError({ field: 'code', message: serverMessage });
            } else if (error?.code === 'NETWORK_ERROR') {
                setFormError({ message: label('wms.collector.error_network') });
            } else {
                setFormError({ message: error?.message || 'Erro ao salvar' });
            }
        } finally {
            setSaving(false);
        }
    };

    const kindStep = (
        <div className="space-y-4">
            <p className="text-sm font-medium text-black dark:text-white">
                {label('wms.location_create.is_virtual_question')}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
                <button
                    type="button"
                    onClick={() => pickKind(true)}
                    className="rounded-xl border border-mono-200 p-4 text-left transition-colors hover:border-mono-400 hover:bg-mono-50 dark:border-mono-800 dark:hover:border-mono-600 dark:hover:bg-mono-900"
                >
                    <p className="font-semibold text-black dark:text-white">
                        {label('wms.location_create.virtual_option')}
                    </p>
                    <p className="mt-1 text-sm text-mono-600 dark:text-mono-400">
                        {label('wms.location_create.virtual_option_help')}
                    </p>
                </button>
                <button
                    type="button"
                    onClick={() => pickKind(false)}
                    className="rounded-xl border border-mono-200 p-4 text-left transition-colors hover:border-mono-400 hover:bg-mono-50 dark:border-mono-800 dark:hover:border-mono-600 dark:hover:bg-mono-900"
                >
                    <p className="font-semibold text-black dark:text-white">
                        {label('wms.location_create.physical_option')}
                    </p>
                    <p className="mt-1 text-sm text-mono-600 dark:text-mono-400">
                        {label('wms.location_create.physical_option_help')}
                    </p>
                </button>
            </div>
        </div>
    );

    const formStep = (
        <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-mono-600 dark:text-mono-400">
                {isVirtual
                    ? label('wms.location_create.virtual_option_help')
                    : label('wms.location_create.description')}
            </p>

            {!isVirtual ? (
                <Select
                    label={label('wms.field.type')}
                    value={form.type}
                    onChange={(event) => setField('type', event.target.value)}
                    disabled={saving}
                >
                    {physicalTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Select>
            ) : null}

            <div>
                <Input
                    label={label('wms.field.code')}
                    value={form.code}
                    onChange={(event) => setField('code', event.target.value.toUpperCase())}
                    errorMessage={formError?.field === 'code' ? formError.message : undefined}
                    disabled={saving}
                    autoComplete="off"
                    className={MONO_CLASS}
                />
                {formError?.field !== 'code' ? (
                    <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                        {isVirtual
                            ? label('wms.location_create.virtual_code_help')
                            : label('wms.location_create.code_help')}
                    </p>
                ) : null}
            </div>

            {!isVirtual ? (
                <Input
                    label={label('wms.field.max_weight_kg')}
                    inputMode="decimal"
                    placeholder="ex.: 1000"
                    value={form.maxWeightKg}
                    onChange={(event) => setField('maxWeightKg', event.target.value)}
                    errorMessage={formError?.field === 'maxWeightKg' ? formError.message : undefined}
                    disabled={saving}
                    autoComplete="off"
                />
            ) : null}

            <Input
                label={label('wms.field.sector')}
                value={form.sector}
                onChange={(event) => setField('sector', event.target.value)}
                errorMessage={formError?.field === 'sector' ? formError.message : undefined}
                disabled={saving}
                autoComplete="off"
            />

            <Textarea
                label={label('wms.field.notes')}
                rows={3}
                value={form.notes}
                onChange={(event) => setField('notes', event.target.value)}
                disabled={saving}
            />

            {!isVirtual ? (
                // Passo OBRIGATÓRIO: física só nasce posicionada no mapa.
                <p className="rounded-lg bg-mono-50 px-3 py-2 text-xs text-mono-600 dark:bg-mono-900 dark:text-mono-400">
                    {label('wms.location_create.physical_place_step')}
                </p>
            ) : null}

            {formError && !formError.field ? (
                <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                    {formError.message}
                </p>
            ) : null}

            <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                <Button type="submit" loading={saving} className="flex-1">
                    {isVirtual ? label('wms.common.save') : label('wms.location_create.continue_to_map')}
                </Button>
                <Button type="button" onClick={() => setStep('kind')} variant="ghost" disabled={saving}>
                    {label('wms.location_create.back')}
                </Button>
                <Button type="button" onClick={closeModal} variant="secondary" className="sm:w-auto" disabled={saving}>
                    {label('wms.common.cancel')}
                </Button>
            </div>
        </form>
    );

    return (
        <Modal isOpen={isOpen} onClose={closeModal} title={label('wms.location_create.title')} size="md">
            {step === 'kind' ? kindStep : formStep}
        </Modal>
    );
};

export default LocationCreateModal;
